import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Luggage, Plus, Trash2, Save, RotateCcw, CheckCircle2, AlertTriangle, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/common";
import { SITE } from "../lib/site";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { SECTIONS, TRIP_TYPES, NEVER_CHECK, PERSONAL_ITEM, GOLDEN_RULE } from "../data/packingChecklist";
import { AppTopBar } from "../components/AppScreen";

/* Interactive "What Should I Pack?" checklist — pick trip types, check items off,
   add your own, and keep a running per-trip report. Signed-in users save lists to
   their account; everyone gets automatic localStorage persistence. */

const LS_KEY = "tp_packing_v1";

const emptyList = () => ({ id: null, name: "", trip_types: [], checked: {}, custom_items: [] });

export default function PackingChecklist() {
  const { user } = useAuth();
  const [list, setList] = useState(() => {
    try { return { ...emptyList(), ...JSON.parse(localStorage.getItem(LS_KEY) || "{}") }; }
    catch { return emptyList(); }
  });
  const [saved, setSaved] = useState([]);
  const [customInput, setCustomInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list)); }
    catch (err) { console.warn("Could not persist packing list to localStorage:", err); }
  }, [list]);

  const loadSaved = useCallback(() => {
    if (!user) return;
    api.get("/packing/lists").then((r) => setSaved(r.data.items)).catch(() => {});
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const types = new Set(list.trip_types);
  const visibleSections = useMemo(() => SECTIONS
    .filter((s) => !s.tags || s.tags.some((t) => types.has(t)))
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.tags.length || i.tags.some((t) => types.has(t))) }))
    .filter((s) => s.items.length > 0), [list.trip_types]); // eslint-disable-line react-hooks/exhaustive-deps

  const allIds = useMemo(() => [
    ...visibleSections.flatMap((s) => s.items.map((i) => i.id)),
    ...list.custom_items.map((c) => c.id),
  ], [visibleSections, list.custom_items]);
  const doneCount = allIds.filter((id) => list.checked[id]).length;
  const pct = allIds.length ? Math.round((doneCount / allIds.length) * 100) : 0;

  const toggleType = (t) => setList((s) => ({
    ...s, trip_types: s.trip_types.includes(t) ? s.trip_types.filter((x) => x !== t) : [...s.trip_types, t] }));
  const toggleItem = (id) => setList((s) => ({ ...s, checked: { ...s.checked, [id]: !s.checked[id] } }));
  const addCustom = () => {
    const label = customInput.trim();
    if (!label) return;
    setList((s) => ({ ...s, custom_items: [...s.custom_items, { id: `custom-${Date.now()}`, label }] }));
    setCustomInput("");
  };
  const removeCustom = (id) => setList((s) => ({
    ...s, custom_items: s.custom_items.filter((c) => c.id !== id),
    checked: Object.fromEntries(Object.entries(s.checked).filter(([k]) => k !== id)) }));

  const save = async () => {
    if (!user) { toast.info("Sign in to save lists to your account — your progress is kept on this device meanwhile."); return; }
    if (!list.name.trim()) { toast.error("Give this trip a name first (e.g. 'Paris — August')"); return; }
    setBusy(true);
    try {
      const body = { name: list.name, trip_types: list.trip_types, checked: list.checked, custom_items: list.custom_items };
      const r = list.id
        ? await api.put(`/packing/lists/${list.id}`, body)
        : await api.post("/packing/lists", body);
      setList((s) => ({ ...s, id: r.data.id }));
      toast.success("Packing list saved");
      loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const openList = (l) => setList({ id: l.id, name: l.name, trip_types: l.trip_types || [], checked: l.checked || {}, custom_items: l.custom_items || [] });
  const deleteList = async (l) => {
    if (!window.confirm(`Delete "${l.name}"?`)) return;
    try { await api.delete(`/packing/lists/${l.id}`); if (list.id === l.id) setList(emptyList()); loadSaved(); }
    catch { toast.error("Failed"); }
  };
  const resetChecks = () => setList((s) => ({ ...s, checked: {} }));

  return (
    <div data-testid="packing-page">
      <AppTopBar title="Checklists" />
      <PageHeader eyebrow={`${SITE.name} · Travel Prep`} title="What Should I Pack?" entityCode="PACK"
        subtitle="The universal travel packing checklist — pick your trip type, check items off as you pack, add your own, and keep a running report for every trip." />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">

        <div className="border-2 border-rose-400 bg-rose-50 p-5 mb-8" data-testid="packing-never-check">
          <div className="font-heading font-900 text-rose-800 flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5" /> NEVER put these in checked baggage</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-rose-900">
            {NEVER_CHECK.map((n) => <span key={n}>• {n}</span>)}
          </div>
          <p className="text-xs text-rose-800 mt-2 font-600">Power banks and spare lithium batteries belong in the cabin — keep them readily accessible (e.g. the seat pocket), not buried overhead. Remember to take them with you after landing!</p>
        </div>

        <div className="border border-zinc-300 bg-white p-6 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <label className="block flex-1 min-w-[220px]">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Trip name</span>
              <input value={list.name} onChange={(e) => setList((s) => ({ ...s, name: e.target.value }))}
                data-testid="packing-name-input" placeholder="e.g. Paris — August 2026"
                className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none" />
            </label>
            <div className="flex gap-2">
              <button onClick={save} disabled={busy} data-testid="packing-save-btn"
                className="inline-flex items-center gap-1.5 bg-[#0e7490] text-white px-4 py-2.5 text-sm font-700 hover:bg-cyan-700 disabled:opacity-50">
                <Save className="w-4 h-4" /> {list.id ? "Update list" : "Save list"}
              </button>
              <button onClick={() => setList(emptyList())} data-testid="packing-new-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-2.5 text-sm font-700 text-zinc-700 hover:border-[#0e7490]">
                <Plus className="w-4 h-4" /> New
              </button>
            </div>
          </div>
          <div className="mt-4">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Trip type (select all that apply — base essentials always included)</span>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="packing-type-chips">
              {TRIP_TYPES.map(([t, label]) => (
                <button key={t} onClick={() => toggleType(t)} data-testid={`packing-type-${t}`}
                  className={`px-3 py-1.5 text-sm font-700 border transition-colors ${types.has(t) ? "bg-[#0e7490] border-[#0e7490] text-white" : "bg-white border-zinc-300 text-zinc-700 hover:border-[#0e7490]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {user && saved.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">My saved trips</span>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="packing-saved-lists">
                {saved.map((l) => (
                  <span key={l.id} className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-sm ${list.id === l.id ? "border-[#0e7490] bg-cyan-50 text-[#0e7490] font-700" : "border-zinc-300 bg-white text-zinc-700"}`}>
                    <button onClick={() => openList(l)} data-testid={`packing-open-${l.id}`} className="hover:underline">{l.name}</button>
                    <button onClick={() => deleteList(l)} data-testid={`packing-delete-${l.id}`} aria-label={`Delete ${l.name}`} className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky top-16 z-20 bg-white border border-zinc-300 px-4 py-3 mb-6 flex items-center gap-3" data-testid="packing-progress">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="flex-1 h-2.5 bg-zinc-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${pct}%` }} /></div>
          <span className="text-sm font-700 whitespace-nowrap" data-testid="packing-progress-text">{doneCount} / {allIds.length} packed ({pct}%)</span>
          <button onClick={resetChecks} data-testid="packing-reset-btn" title="Uncheck everything"
            className="inline-flex items-center gap-1 text-xs font-700 text-zinc-500 hover:text-zinc-800"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {visibleSections.map((s) => {
            const done = s.items.filter((i) => list.checked[i.id]).length;
            return (
              <section key={s.id} className="border border-zinc-300 bg-white p-5" data-testid={`packing-section-${s.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h2 className="font-heading font-700 text-base">{s.title}</h2>
                  <span className={`text-xs font-700 px-1.5 py-0.5 ${done === s.items.length ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{done}/{s.items.length}</span>
                </div>
                {s.tip && <p className="text-xs text-muted-foreground mb-2">{s.tip}</p>}
                <div className="space-y-1 mt-2">
                  {s.items.map((i) => (
                    <label key={i.id} className={`flex items-start gap-2.5 text-sm cursor-pointer py-0.5 ${list.checked[i.id] ? "text-zinc-400 line-through" : "text-zinc-800"}`}>
                      <input type="checkbox" checked={!!list.checked[i.id]} onChange={() => toggleItem(i.id)}
                        data-testid={`packing-item-${i.id}`} className="w-4 h-4 mt-0.5 accent-[#0e7490] shrink-0" />
                      <span>{i.label}</span>
                    </label>
                  ))}
                </div>
              </section>
            );
          })}

          <section className="border border-zinc-300 bg-white p-5" data-testid="packing-section-custom">
            <h2 className="font-heading font-700 text-base mb-1">My Own Items</h2>
            <p className="text-xs text-muted-foreground mb-2">Anything specific to this trip — chargers for special gear, gifts, work items…</p>
            <div className="flex gap-2 mb-3">
              <input value={customInput} onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustom()} data-testid="packing-custom-input"
                placeholder="Add an item…" className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none" />
              <button onClick={addCustom} data-testid="packing-custom-add" className="bg-zinc-800 text-white px-3 py-2 text-sm font-700 hover:bg-zinc-700"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1">
              {list.custom_items.map((c) => (
                <div key={c.id} className="flex items-start gap-2.5 text-sm py-0.5">
                  <input type="checkbox" checked={!!list.checked[c.id]} onChange={() => toggleItem(c.id)}
                    data-testid={`packing-item-${c.id}`} className="w-4 h-4 mt-0.5 accent-[#0e7490] shrink-0" />
                  <span className={`flex-1 ${list.checked[c.id] ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{c.label}</span>
                  <button onClick={() => removeCustom(c.id)} data-testid={`packing-custom-remove-${c.id}`} className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {list.custom_items.length === 0 && <p className="text-xs text-muted-foreground">No custom items yet.</p>}
            </div>
          </section>
        </div>

        <div className="border border-sky-300 bg-sky-50/60 p-5 mt-8" data-testid="packing-personal-item">
          <div className="font-heading font-700 flex items-center gap-2 mb-2"><Briefcase className="w-5 h-5 text-sky-700" /> The "Personal Item" Survival Strategy</div>
          <p className="text-sm text-zinc-700 mb-2">Your personal item is your survival bag — if your checked suitcase disappears, you should still be able to function for 24 hours. Always keep with you:</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-800">
            {PERSONAL_ITEM.map((p) => <span key={p}>• {p}</span>)}
          </div>
        </div>

        <div className="border-2 border-amber-400 bg-amber-50 p-5 mt-6 text-center" data-testid="packing-golden-rule">
          <div className="font-heading font-900 text-amber-900 flex items-center justify-center gap-2 mb-1"><Luggage className="w-5 h-5" /> Golden Rule</div>
          <p className="text-sm font-600 text-amber-900 max-w-2xl mx-auto">{GOLDEN_RULE}</p>
        </div>

        <p className="text-xs text-muted-foreground mt-6">
          Some items — especially medicines, batteries, liquids and medical devices — have airline and security restrictions.
          Check the applicable airline and destination rules. See the full guide on our <Link to="/faq" className="font-700 text-primary hover:underline">FAQ page</Link>,
          our <Link to="/baggage" className="font-700 text-primary hover:underline">Baggage Fees database</Link> and
          the <Link to="/visas" className="font-700 text-primary hover:underline">Trip Document Checker</Link>.
        </p>
      </div>
    </div>
  );
}
